import React, { useMemo, useState } from "react";
import { bulkUpdate, anonymizeFiles } from "../api/dicomApi";

const FIELDS = [
  {
    key: "patientName",
    label: "Patient Name",
    placeholder: "Enter patient name",
  },
  {
    key: "patientId",
    label: "Patient ID",
    placeholder: "Enter patient ID",
  },
  {
    key: "studyDate",
    label: "Study Date",
    placeholder: "YYYYMMDD",
  },
  {
    key: "institutionName",
    label: "Hospital / Institution",
    placeholder: "Enter institution",
  },
];


/* ================================================================
   ICONS
================================================================ */

function CheckIcon({ className = "w-4 h-4" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path
        d="m5 12 4 4L19 6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}


function CloseIcon({ className = "w-4 h-4" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path
        d="M6 6l12 12M18 6 6 18"
        strokeLinecap="round"
      />
    </svg>
  );
}


function EditIcon({ className = "w-5 h-5" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <path
        d="M12 20h9"
        strokeLinecap="round"
      />

      <path
        d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4L16.5 3.5Z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}


function ShieldIcon({ className = "w-5 h-5" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <path
        d="M12 3 20 6v6c0 5-3.2 8.2-8 10-4.8-1.8-8-5-8-10V6l8-3Z"
        strokeLinejoin="round"
      />

      <path
        d="m8.5 12 2.2 2.2 4.8-5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}


function PrinterIcon({ className = "w-5 h-5" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <path
        d="M6 9V4h12v5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      <path
        d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"
        strokeLinecap="round"
      />

      <path
        d="M6 14h12v7H6z"
        strokeLinejoin="round"
      />

      <path
        d="M17 12h.01"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
    </svg>
  );
}


function ChevronIcon({ className = "w-4 h-4" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path
        d="m6 9 6 6 6-6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}


function Spinner({ className = "w-4 h-4" }) {
  return (
    <svg
      className={`${className} animate-spin`}
      viewBox="0 0 24 24"
      fill="none"
    >
      <circle
        cx="12"
        cy="12"
        r="9"
        stroke="currentColor"
        strokeWidth="3"
        className="opacity-25"
      />

      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}


/* ================================================================
   MAIN COMPONENT
================================================================ */

export default function BulkEditToolbar({
  selectedIds,
  onApplied,
  onClear,
  onPrint,
}) {
  const [values, setValues] = useState({});
  const [applying, setApplying] = useState(false);
  const [anonymizing, setAnonymizing] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const activeFields = useMemo(
    () =>
      Object.entries(values).filter(
        ([, value]) =>
          value !== undefined &&
          value !== null &&
          String(value).trim() !== ""
      ),
    [values]
  );


  /* ==============================================================
     APPLY BULK UPDATE
  ============================================================== */

  const apply = async () => {
    if (activeFields.length === 0 || applying) return;

    setApplying(true);

    try {
      const fields = Object.fromEntries(activeFields);

      await bulkUpdate(selectedIds, fields);

      onApplied?.();

      setValues({});
    } finally {
      setApplying(false);
    }
  };


  /* ==============================================================
     ANONYMIZE
  ============================================================== */

  const anonymize = async () => {
    if (anonymizing) return;

    const confirmed = window.confirm(
      `Anonymize ${selectedIds.length} file(s)? This permanently replaces patient name, ` +
        `birth date, sex, address, institution, and referring/operator names on the actual ` +
        `DICOM files with a new random Patient ID. This cannot be undone.`
    );

    if (!confirmed) return;

    setAnonymizing(true);

    try {
      await anonymizeFiles(selectedIds);

      onApplied?.();
    } finally {
      setAnonymizing(false);
    }
  };


  /* ==============================================================
     UPDATE FIELD
  ============================================================== */

  const updateField = (key, value) => {
    setValues((previous) => ({
      ...previous,
      [key]: value,
    }));
  };


  /* ==============================================================
     CLEAR
  ============================================================== */

  const clearAll = () => {
    setValues({});
    onClear?.();
  };


  return (
    <div
      className="
        w-full
        overflow-hidden
        rounded-2xl
        border
        border-[#d9ebe8]
        bg-white
        shadow-[0_5px_22px_rgba(37,85,95,0.07)]
      "
    >

      {/* ==========================================================
          TOOLBAR HEADER
      ========================================================== */}

      <div
        className="
          flex
          flex-col
          sm:flex-row
          sm:items-center
          sm:justify-between
          gap-4
          px-5
          py-4
          bg-gradient-to-r
          from-[#f0faf8]
          via-white
          to-[#f5fafc]
          border-b
          border-[#e2eeed]
        "
      >

        {/* Left */}
        <div className="flex items-center gap-3">

          <div
            className="
              flex
              items-center
              justify-center
              w-10
              h-10
              rounded-xl
              bg-[#dff4f0]
              text-[#12978c]
            "
          >
            <EditIcon />
          </div>


          <div>

            <div className="flex items-center gap-2">

              <h3 className="text-sm font-semibold text-[#304b59]">
                Bulk Edit
              </h3>

              <span
                className="
                  inline-flex
                  items-center
                  px-2
                  py-0.5
                  rounded-full
                  bg-[#dff4f0]
                  text-[#138c82]
                  text-[10px]
                  font-semibold
                "
              >
                {selectedIds.length} selected
              </span>

            </div>

            <p className="text-[11px] text-[#8798a3] mt-0.5">
              Update metadata for multiple DICOM files
            </p>

          </div>
        </div>


        {/* Right */}
        <div className="flex items-center gap-2">

          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            className="
              inline-flex
              items-center
              gap-1.5
              px-3
              py-2
              rounded-lg
              border
              border-[#dbe6ea]
              bg-white
              text-xs
              font-medium
              text-[#687985]
              hover:bg-[#f6fafb]
              hover:text-[#2b718f]
              transition-all
            "
          >
            {expanded ? "Collapse" : "Edit fields"}

            <ChevronIcon
              className={`w-3.5 h-3.5 transition-transform ${
                expanded ? "rotate-180" : ""
              }`}
            />
          </button>


          <button
            type="button"
            onClick={clearAll}
            className="
              inline-flex
              items-center
              gap-1.5
              px-3
              py-2
              rounded-lg
              border
              border-[#e2e9ed]
              bg-white
              text-xs
              font-medium
              text-[#7c8b95]
              hover:bg-[#fff7f7]
              hover:text-[#c45656]
              hover:border-[#f0d5d5]
              transition-all
            "
          >
            <CloseIcon className="w-3.5 h-3.5" />
            Clear
          </button>

        </div>

      </div>


      {/* ==========================================================
          EDIT AREA
      ========================================================== */}

      {expanded && (
        <div className="p-5">

          {/* Information message */}
          <div
            className="
              flex
              items-start
              gap-3
              rounded-xl
              bg-[#f7fafb]
              border
              border-[#e5edf1]
              px-4
              py-3
              mb-5
            "
          >

            <div className="flex-shrink-0 mt-0.5">

              <svg
                className="w-4 h-4 text-[#6f96a6]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
              >
                <circle cx="12" cy="12" r="9" />

                <path
                  d="M12 10v6"
                  strokeLinecap="round"
                />

                <path
                  d="M12 7h.01"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
              </svg>

            </div>

            <p className="text-xs leading-5 text-[#7b8b95]">
              Enter only the metadata fields you want to change.
              Blank fields will be ignored and will not modify the
              selected DICOM files.
            </p>

          </div>


          {/* ========================================================
              FIELD GRID
          ======================================================== */}

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">

            {FIELDS.map((field) => {

              const hasValue =
                values[field.key] !== undefined &&
                values[field.key] !== "";

              return (
                <div
                  key={field.key}
                  className={`
                    rounded-xl
                    border
                    p-3.5
                    transition-all
                    ${
                      hasValue
                        ? "border-[#9ddbd3] bg-[#f5fbfa]"
                        : "border-[#e2e9ed] bg-white"
                    }
                  `}
                >

                  {/* Label */}
                  <div className="flex items-center justify-between mb-2">

                    <label
                      htmlFor={`bulk-${field.key}`}
                      className="
                        text-[11px]
                        font-semibold
                        uppercase
                        tracking-[0.07em]
                        text-[#6e7e89]
                      "
                    >
                      {field.label}
                    </label>

                    {hasValue && (
                      <span className="text-[#12998d]">
                        <CheckIcon className="w-3.5 h-3.5" />
                      </span>
                    )}

                  </div>


                  {/* Input */}
                  <input
                    id={`bulk-${field.key}`}
                    type="text"
                    value={values[field.key] || ""}
                    onChange={(e) =>
                      updateField(field.key, e.target.value)
                    }
                    placeholder={field.placeholder}
                    className="
                      w-full
                      h-10
                      px-3
                      rounded-lg
                      border
                      border-[#dce5e9]
                      bg-white
                      text-sm
                      text-[#344b59]
                      placeholder:text-[#a5b0b7]
                      outline-none
                      transition-all
                      focus:border-[#39a99c]
                      focus:ring-4
                      focus:ring-[#39a99c]/10
                    "
                  />

                </div>
              );
            })}

          </div>


          {/* ========================================================
              ACTIONS
          ======================================================== */}

          <div
            className="
              mt-5
              pt-5
              border-t
              border-[#e8eef1]
              flex
              flex-col
              xl:flex-row
              xl:items-center
              xl:justify-between
              gap-4
            "
          >

            {/* Left helper */}
            <div className="flex items-center gap-2">

              <div className="flex items-center justify-center w-7 h-7 rounded-full bg-[#edf7f6]">

                <CheckIcon className="w-4 h-4 text-[#15988d]" />

              </div>

              <p className="text-xs text-[#7d8d97]">

                {activeFields.length > 0 ? (
                  <>
                    <span className="font-semibold text-[#5e717d]">
                      {activeFields.length}
                    </span>{" "}
                    field{activeFields.length !== 1 ? "s" : ""} ready
                    to update
                  </>
                ) : (
                  "No fields selected for update"
                )}

              </p>

            </div>


            {/* Action buttons */}
            <div className="flex flex-wrap items-center gap-2.5">

              {/* Apply */}
              <button
                type="button"
                onClick={apply}
                disabled={
                  applying ||
                  anonymizing ||
                  activeFields.length === 0
                }
                className="
                  inline-flex
                  items-center
                  justify-center
                  gap-2
                  min-h-10
                  px-5
                  rounded-lg
                  bg-gradient-to-r
                  from-[#11a991]
                  to-[#08a488]
                  hover:from-[#0d9b85]
                  hover:to-[#078f7c]
                  text-white
                  text-xs
                  font-semibold
                  shadow-[0_5px_14px_rgba(13,164,142,0.20)]
                  transition-all
                  disabled:opacity-45
                  disabled:cursor-not-allowed
                "
              >

                {applying ? (
                  <>
                    <Spinner />
                    Applying changes...
                  </>
                ) : (
                  <>
                    <CheckIcon />
                    Apply to {selectedIds.length} files
                  </>
                )}

              </button>


              {/* Print */}
              <button
                type="button"
                onClick={onPrint}
                disabled={applying || anonymizing}
                className="
                  inline-flex
                  items-center
                  justify-center
                  gap-2
                  min-h-10
                  px-4
                  rounded-lg
                  border
                  border-[#d8e3e8]
                  bg-white
                  text-[#5c707c]
                  text-xs
                  font-semibold
                  hover:bg-[#f5f9fb]
                  hover:border-[#c7d9e1]
                  hover:text-[#266f92]
                  transition-all
                  disabled:opacity-50
                  disabled:cursor-not-allowed
                "
                title="Lay out selected images on a printable film sheet"
              >

                <PrinterIcon className="w-4 h-4" />

                Print film layout

              </button>

            </div>

          </div>


          {/* ========================================================
              ANONYMIZATION
          ======================================================== */}

          <div
            className="
              mt-4
              rounded-xl
              border
              border-[#f1dddd]
              bg-[#fffafa]
              p-4
            "
          >

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">

              {/* Info */}
              <div className="flex items-start gap-3">

                <div
                  className="
                    flex
                    items-center
                    justify-center
                    flex-shrink-0
                    w-9
                    h-9
                    rounded-lg
                    bg-[#fdeaea]
                    text-[#c75c5c]
                  "
                >
                  <ShieldIcon />
                </div>


                <div>

                  <h4 className="text-xs font-semibold text-[#714d4d]">
                    Anonymize selected files
                  </h4>

                  <p className="mt-1 max-w-[680px] text-[11px] leading-5 text-[#9b7777]">
                    Permanently remove identifying patient and
                    institution information and assign a new random
                    Patient ID. This operation cannot be undone.
                  </p>

                </div>

              </div>


              {/* Anonymize button */}
              <button
                type="button"
                onClick={anonymize}
                disabled={anonymizing || applying}
                className="
                  inline-flex
                  items-center
                  justify-center
                  gap-2
                  min-h-10
                  px-4
                  rounded-lg
                  border
                  border-[#e8bcbc]
                  bg-white
                  text-[#bd5959]
                  text-xs
                  font-semibold
                  hover:bg-[#fff2f2]
                  hover:border-[#dca5a5]
                  transition-all
                  disabled:opacity-45
                  disabled:cursor-not-allowed
                "
                title="Strip patient and institution identifiers"
              >

                {anonymizing ? (
                  <>
                    <Spinner />
                    Anonymizing...
                  </>
                ) : (
                  <>
                    <ShieldIcon className="w-4 h-4" />
                    Anonymize {selectedIds.length} files
                  </>
                )}

              </button>

            </div>

          </div>

        </div>
      )}


      {/* ==========================================================
          COLLAPSED STATE
      ========================================================== */}

      {!expanded && (
        <div className="px-5 py-3.5">

          <div className="flex flex-wrap items-center justify-between gap-3">

            <p className="text-xs text-[#82919b]">
              Bulk edit panel collapsed.
              <span className="ml-1 font-medium text-[#61747f]">
                {activeFields.length} field
                {activeFields.length !== 1 ? "s" : ""} configured.
              </span>
            </p>


            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="
                text-xs
                font-semibold
                text-[#168d85]
                hover:text-[#0f746d]
                transition-colors
              "
            >
              Open bulk editor →
            </button>

          </div>

        </div>
      )}

    </div>
  );
}